import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { format } from 'date-fns';
import { NzModalService } from 'ng-zorro-antd/modal';
import {
    NzTableFilterFn,
    NzTableFilterList,
    NzTableSortFn,
} from 'ng-zorro-antd/table';
import { debounceTime, fromEvent, Subscription } from 'rxjs';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import {
    DataLoaderResult,
    IDataLoaderResult,
} from 'src/app/services/data-loader/data-loader-result';
import { IQueryContext } from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import { IDashboardItem } from 'src/app/utils/dashboard-item';
import { AfterMethod } from 'src/app/utils/decorators/after-method';
import { getDefaultDataExporter } from 'src/app/utils/echarts/data-export';

export type BITableOptions = {
    columns?: TableColumnItem[];
    dataset?: { data: TableDataItem[] };
};

export interface TableDataItem {
    index: number;
    data: {
        [key: string]: {
            label: string;
            value: string | number;
            'background-color'?: string;
        };
    };
    /** Opt-in: render the row dimmed + struck-through while keeping it interactive. */
    muted?: boolean;
    /** Opt-in: arbitrary consumer payload surfaced back through {@link TableDashboardItemComponent.rowClick}. */
    meta?: unknown;
    /**
     * Opt-in tree/expandable support (all default off → flat tables render unchanged).
     * `children` nests rows one level deeper; the component flattens the tree into the visible list,
     * showing a node's children only while its key is expanded. `expandable` renders a caret in the
     * tree column (see {@link TableColumnItem.treeColumn}); `level` drives the indent; `key` is the
     * stable identity used to remember expand state across data re-emits. When any row carries these,
     * the component disables free column sort so parent/child grouping is preserved (rows are expected
     * pre-ordered). Tree tables should use `viewMode: 'scroll'` (paging would split parents from kids).
     */
    children?: TableDataItem[];
    expandable?: boolean;
    level?: number;
    key?: string;
}

export interface TableColumnItem {
    id: string;
    name: string;
    nzLeft: boolean;
    nzSortFn: NzTableSortFn<TableDataItem>;
    nzAlign?: 'left' | 'right';
    nzWidth?: string;
    nzSortPriority?: number;
    /** Whether column can be filtered */
    canFilter?: boolean;
    /** Filter function */
    nzFilterFn?: NzTableFilterFn;
    listOfFilter?: NzTableFilterList;
    /** used to format label */
    labelFormatter?: (value: any) => string;
    /** min of column value */
    min?: number;
    /** max of column value */
    max?: number;
    colorPalette?: {
        startColor: string;
        endColor: string;
        steps: number;
    };
    /**
     * Opt-in: the column that renders the tree indent + expand caret for expandable rows (see
     * {@link TableDataItem.children}). If no column sets it, the first column is used. No effect on
     * flat tables.
     */
    treeColumn?: boolean;
}

export interface ITableDashboardItemComponent extends Omit<
    IDashboardItem,
    'composeData'
> {
    /** Parent dashboard name */
    dashboardName: string;
    /**
     * Scroll-mode ('scroll') scroll-body height, any CSS length (e.g. '360px'). When set it fixes the
     * internal scroll height instead of auto-filling to the viewport bottom — required for scroll tables
     * placed low on a scrolling dashboard, where auto-fill would collapse them to ~0. Ignored in 'paged'.
     */
    height?: string;
    /** Table title */
    title: string | ((context: IQueryContext) => string);
    /** Table subtitle */
    subtitle?: string;
    /** A function that composes data prepared for table from raw data */
    composeData: (rawData: any[], context?: IQueryContext) => BITableOptions;
    /** A group of utility tools. */
    toolbox?: {
        /** The configuration item for each tool. */
        feature?: {
            /** Export data to csv*/
            exportDataToCSV?: {
                /** Name of exported file. */
                name?: string;
                /** A function that transforms EChartsOption to a 2D array. */
                dataExporter?: (echartsOptions: any) => void;
            };
            /** Export data to xlsx*/
            exportDataToXLSX?: {
                /** Name of exported file. */
                name?: string;
                /** A function that transforms EChartsOption to a 2D array. */
                dataExporter?: (echartsOptions: any) => void;
            };
        };
    };
    /** ng-zorro specific. Table page size */
    nzPageSize?: number;
    /** A property that allows you to choose between a table divided into pages and a table that dynamically adjusts to the size of the browser window with scrolling.
     *
     * @default 'paged'
     */
    viewMode?: 'paged' | 'scroll';
    /** HTML string for chart documentation */
    chartDocumentation?: string;
}

@Component({
    selector: 'app-table-dashboard-item',
    templateUrl: './table-dashboard-item.component.html',
    styleUrls: ['./table-dashboard-item.component.scss'],
    // Avoid checking scroll-mode tables on every app tick.
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableDashboardItemComponent implements OnInit, AfterViewInit {
    @Input()
    dashboardItem!: ITableDashboardItemComponent;

    @Input()
    dataLoaderEmitter!: EventEmitter<IDataLoaderResult>;

    /**
     * Opt-in: emits the clicked row. Rows render clickable (pointer cursor) only when this output has a
     * subscriber, so tables that don't bind it behave exactly as before.
     */
    @Output()
    rowClick = new EventEmitter<TableDataItem>();

    @ViewChild('tableContainer', { static: true })
    containerRef!: ElementRef<HTMLDivElement>;

    dataLoaderResult: IDataLoaderResult = DataLoaderResult.getLoading(
        [],
        QueryContextService.EMPTY
    );

    // nzScrollY!: number;
    // getNzScrollY() {
    //     return `${this.nzScrollY}px`;
    // }
    // @ViewChild('tableContainer', { static: true }) _tableContainer!: ElementRef;
    // getTableContainerHeight() {
    //     setTimeout(() => {
    //         const nzScrollY =
    //             (this._tableContainer.nativeElement as HTMLImageElement)
    //                 .clientHeight - 24; // X depend of your page display
    //                 this.nzScrollY = nzScrollY;
    //     });
    // }

    // @HostListener('window:resize')
    // @HostListener('window:zoom')
    // onWindowResizeOrZoom() {
    //     this.getTableContainerHeight();
    // }

    tableOptions: BITableOptions = {};

    copySQLDisabled = true;

    // error handling
    showErrorIcon: boolean = false;
    showErrorOverlay: boolean = false;
    errorTitle: string = '';
    errorMessage: string = '';

    isLoading: boolean = true;

    // Dynamic table size
    resizeSub?: Subscription;
    tableScrollY?: string;

    listOfData: TableDataItem[] = [];
    trackByIndex(_: number, data: TableDataItem): number {
        return data.index;
    }

    /** Source tree (top-level rows, each may carry `children`); `listOfData` is its visible flattening. */
    private treeData: TableDataItem[] = [];
    /** Expanded row keys, preserved across data re-emits (breakdown tables re-emit on zoom/toggle). */
    expandedKeys = new Set<string>();
    /** True when the current dataset uses expandable rows → free column sort is suppressed. */
    isTreeMode = false;
    /** Cached tree column so the cell template does not repeatedly search all columns. */
    private treeColumn?: TableColumnItem;

    private setTreeData(data: TableDataItem[]): void {
        this.treeData = data;
        this.isTreeMode = this.treeData.some((node) => !!node.children?.length);
        const columns = this.tableOptions.columns ?? [];
        this.treeColumn =
            columns.find((column) => column.treeColumn) ?? columns[0];
        this.refreshVisibleRows();
    }

    private refreshVisibleRows(): void {
        this.listOfData = this.flattenVisible(this.treeData);
    }

    /** Flatten the tree into the visible row list: a node's children show only while its key is expanded. */
    private flattenVisible(
        nodes: TableDataItem[],
        level = 0,
        out: TableDataItem[] = []
    ): TableDataItem[] {
        for (const node of nodes) {
            node.level = level;
            node.expandable = !!(
                node.children &&
                node.children.length &&
                node.key
            );
            out.push(node);
            if (node.expandable && this.expandedKeys.has(node.key!)) {
                this.flattenVisible(node.children!, level + 1, out);
            }
        }
        return out;
    }

    /** Toggle an expandable row's expanded state and re-flatten (leaves every other row's state intact). */
    toggleExpand(row: TableDataItem): void {
        if (!row.key) {
            return;
        }
        if (this.expandedKeys.has(row.key)) {
            this.expandedKeys.delete(row.key);
        } else {
            this.expandedKeys.add(row.key);
        }
        this.refreshVisibleRows();
    }

    isExpanded(row: TableDataItem): boolean {
        return !!row.key && this.expandedKeys.has(row.key);
    }

    /** Whether this column owns the tree indent/caret. */
    isTreeColumn(column: TableColumnItem): boolean {
        return this.isTreeMode && column === this.treeColumn;
    }

    treeIndent(row: TableDataItem, column: TableColumnItem): number | null {
        return this.isTreeColumn(column) ? (row.level ?? 0) * 16 + 4 : null;
    }

    treeCaret(row: TableDataItem): string {
        if (!row.expandable) {
            return '';
        }
        return this.isExpanded(row) ? '▾' : '▸';
    }

    /** Suppress free column sort in tree mode so parent/child grouping stays intact (rows pre-ordered). */
    getColumnSortFn(
        column: TableColumnItem
    ): NzTableSortFn<TableDataItem> | null {
        return this.isTreeMode ? null : column.nzSortFn;
    }

    @AfterMethod((self) => self.setTableHeight())
    private dataLoaderSubscriber(dataLoaderResult: IDataLoaderResult) {
        this.dataLoaderResult = dataLoaderResult;
        if (dataLoaderResult.isLoading) {
            this.showLoading();
        } else {
            this.hideLoading();
            // if status not ok or loading
            if (dataLoaderResult.status != 'ok') {
                // if errorType = 'unsupportedColumn' then show error message
                // otherwise show statusMessage
                if (dataLoaderResult.status == 'unsupported-column') {
                    this.showOverlayMessage(
                        'Unsupported filter',
                        dataLoaderResult.statusMessage
                    );
                } else {
                    this.showErrorIcon = true;
                    this.showOverlayMessage(
                        'Error',
                        dataLoaderResult.statusMessage
                    );
                }
            } else {
                this.showErrorIcon = false;
            }
        }
        // console.log({
        //     msg: `[EChartsDashboardItem][${this.dashboardItem.instanceId}] Got dataLoaderResult`,
        //     dataLoaderResult,
        // });

        // if (this.chartInstance) {
        //     this.applyDataLoaderResult(dataLoaderResult);
        if (this.dataLoaderResult.status === 'ok') {
            this.hideOverlayMessage();
            this.tableOptions = this.dashboardItem.composeData(
                this.dataLoaderResult.data,
                this.dataLoaderResult.queryContext
            );
            // Tree-aware: `dataset.data` is the top-level rows (each may carry `children`). Keep expand
            // state across re-emits and render only the visible flattening; flat tables are unaffected.
            this.setTreeData(this.tableOptions.dataset!.data);
            // console.log({
            //     msg: `[TableDashboardItemComponent][${this.dashboardItem.instanceId}] Got dataLoaderResult`,
            //     dataLoaderResult,
            //     data,
            // });
        }
        this.changeDetectorRef.markForCheck();
    }

    constructor(
        private modal: NzModalService,
        private changeDetectorRef: ChangeDetectorRef
    ) {
        this.dataLoaderSubscriber = this.dataLoaderSubscriber.bind(this);
    }

    ngOnInit(): void {
        this.dataLoaderEmitter.subscribe(this.dataLoaderSubscriber);
    }

    ngAfterViewInit(): void {
        this.resizeSub = fromEvent(window, 'resize')
            .pipe(debounceTime(120))
            .subscribe(() => {
                this.setTableHeight();
                this.changeDetectorRef.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this.resizeSub?.unsubscribe();
        this.dataLoaderEmitter.unsubscribe();
    }

    canShowData() {
        return this.dataLoaderResult.status === 'ok';
    }

    showLoading() {
        this.isLoading = true;
    }

    hideLoading() {
        this.isLoading = false;
    }

    getTableHeight() {
        return this.dashboardItem.height ?? '600px';
    }

    public copySQLToCliboard() {
        if (this.dataLoaderResult) {
            // this.clipboard.copy(this.dataLoaderResult.sqlAndJsProcessing);
            // this._snackBar.open('Source SQL copied to clipboard', 'OK', {
            //     duration: 3000,
            // });
        } else {
            // this._snackBar.open('Error. Missing data to copy.', 'OK', {
            //     duration: 3000,
            // });
        }
    }

    public copyStatusMessage() {
        if (this.dataLoaderResult) {
            // console.log(`${this.dataLoaderResult.statusMessage}`);
            // this.clipboard.copy(this.dataLoaderResult.statusMessage);
            // this._snackBar.open('statusMessage copied to clipboard', 'OK', {
            //     duration: 3000,
            // });
        } else {
            // this._snackBar.open('Error. Missing data to copy.', 'OK', {
            //     duration: 3000,
            // });
        }
    }

    public canShowCardTitle() {
        if (typeof this.dashboardItem.title === 'function') {
            return !!this.dashboardItem.title(
                this.dataLoaderResult.queryContext
            );
        }
        return !!this.dashboardItem.title;
    }

    public getCardTitle() {
        if (typeof this.dashboardItem.title === 'function') {
            return this.dashboardItem.title(this.dataLoaderResult.queryContext);
        }
        return this.dashboardItem.title;
    }

    public canShowCardSubTitle() {
        return !!this.dashboardItem.subtitle;
    }

    public canShowExportDataToCSV() {
        if (
            !!this.dashboardItem.toolbox &&
            !!this.dashboardItem.toolbox.feature &&
            !this.dashboardItem.toolbox.feature?.exportDataToCSV
        ) {
            return false;
        }

        return true;
    }

    public canShowExportDataToXLSX() {
        if (
            !!this.dashboardItem.toolbox &&
            !!this.dashboardItem.toolbox.feature &&
            !this.dashboardItem.toolbox.feature?.exportDataToXLSX
        ) {
            return false;
        }

        return true;
    }

    /**
     * Exports the currently visible data to CSV.
     */
    public exportVisibleDataToCSV() {
        let item = this.dashboardItem;

        const chartTitle =
            this.getCardTitle()?.toLowerCase().replace(/ /g, '_') ??
            'chart_data';
        // // Use the format function to format the date
        const formattedDate = format(new Date(), 'yyyyMMdd_HHmmss');
        const filename = `${item.dashboardName}_${chartTitle}_${formattedDate}`;

        const defaultCreator = () =>
            getDefaultDataExporter({
                type: 'csv',
                filename,
                dataExporter: TableDashboardItemComponent.tableDataExporter,
            });
        // if item
        // if toolbox is not present in item or exportDataToCSV is not present in feature, use default dataExporter
        const exporter =
            item.toolbox?.feature?.exportDataToCSV?.dataExporter ??
            defaultCreator();
        exporter(this.tableOptions);
    }

    /**
     * Exports the currently visible data to Excel.
     */
    public exportVisibleDataToXLSX() {
        let item = this.dashboardItem;

        const chartTitle =
            this.getCardTitle()?.toLowerCase().replace(/ /g, '_') ??
            'chart_data';
        // Use the format function to format the date
        const formattedDate = format(new Date(), 'yyyyMMdd_HHmmss');
        const filename = `${item.dashboardName}_${chartTitle}_${formattedDate}`;
        const defaultCreator = () =>
            getDefaultDataExporter({
                type: 'xlsx',
                filename,
                dataExporter: TableDashboardItemComponent.tableDataExporter,
            });

        // if itemOptions.toolbox is not present in item or itemOptions.toolbox.feature.exportDataToCSV is not present, use defaultCreator
        const exporter =
            item.toolbox?.feature?.exportDataToXLSX?.dataExporter ??
            defaultCreator();
        exporter(this.tableOptions);
    }

    showOverlayMessage(title: string, message: string) {
        this.errorTitle = title;
        this.errorMessage = message;
        this.showErrorOverlay = true;
    }

    hideOverlayMessage() {
        this.showErrorOverlay = false;
    }

    isError() {
        return this.showErrorOverlay;
    }

    private setTableHeight(): void {
        // Explicit height wins → fixed scroll-body height. Needed for scroll tables placed low on a
        // scrolling dashboard: the viewport auto-fill below would resolve to ~0 (top past the fold).
        if (this.dashboardItem?.height) {
            this.tableScrollY = this.dashboardItem.height;
            return;
        }

        const container = this.containerRef.nativeElement;

        // Distance between the top edge of the container and the top edge of the viewport
        const top = container.getBoundingClientRect().top;

        const vh = window.innerHeight;
        const bottomPadding = 50;

        const h = Math.max(0, Math.floor(vh - top - bottomPadding));
        this.tableScrollY = `${h}px`;
    }

    /**
     * Exports the currently visible data from BITableOptions instance into a 2D array.
     * @param {BITableOptions} tableOptions - The ECharts chart instance options.
     * @returns {Array<Array>} A 2D array of the exported data.
     */
    static tableDataExporter(tableOptions: BITableOptions): any[][] {
        const data = tableOptions.dataset!.data;
        const columns = tableOptions.columns!;
        const header = columns.map((column) => column.name);
        const body = data.map((row) =>
            columns.map((column) => {
                if (column.id in row.data && 'value' in row.data[column.id]) {
                    return row.data[column.id].value;
                }
                return;
            })
        );
        return [header, ...body];
    }

    public showChartDocumentation() {
        DashboardService.showChartDocumentation(
            this.modal,
            this.getCardTitle(),
            this.dashboardItem.chartDocumentation
        );
    }
}
